/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Select, MenuItem, FormControl, CircularProgress, IconButton } from '@material-ui/core'
import { KeyboardArrowDownRounded } from '@material-ui/icons'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment from 'config/moment'
import { DonutTagChart } from 'features/Main/NewDashboard/components/DonutTagChart'
import { useApiCall } from 'hooks/useApi'
import { montserratStyle } from 'features/Main/NewDashboard/styles'
import { formatDonutChartData } from 'utils/chart'
import CustomTooltip from 'features/Main/NewDashboard/components/CustomTooltip'

import { useLoadingTracker } from '../../../hooks/useLoadingTracker'
import {
  getFoodPreferenceAnalysisByLevelDetailed,
  FoodPreferenceData,
  getUserPollQuestions,
  UserPollQuestion,
  UserPollQuestionsResponse,
} from './api'

const foodColors = ['teal', 'sky', 'green', 'emerald', 'lime']
const emptyDataColor = ['gray']

interface FoodPreferenceDonutChartsByLevelProps {
  period: { month: number; year: number }
  storeId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const FoodPreferenceDonutChartsByLevel: React.FC<FoodPreferenceDonutChartsByLevelProps> = ({
  period,
  storeId,
  trigger,
  onLoaded,
}) => {
  const [dataByLevel, setDataByLevel] = useState<{ [level: number]: FoodPreferenceData[] }>({})
  const [levels, setLevels] = useState<number[]>([])
  const [questions, setQuestions] = useState<UserPollQuestion[]>([])
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null)
  const canFetchRef = useRef(false)
  const skipQuestionFetchRef = useRef(true)
  const pendingInitialLoadRef = useRef(false)
  const [statsCall, statsLoading] = useApiCall<
    { startDate: string; endDate: string; storeId?: number; questionId?: number },
    FoodPreferenceData[]
  >(getFoodPreferenceAnalysisByLevelDetailed)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'FoodPreferenceDonutChartsByLevel' })
  const [questionsCall, questionsLoading] = useApiCall<
    Record<string, never>,
    UserPollQuestionsResponse
  >(getUserPollQuestions)

  const handleQuestionChange = useCallback((e: React.ChangeEvent<{ value: unknown }>) => {
    const questionId = parseInt(e.target.value as string, 10)
    setSelectedQuestionId(questionId)
  }, [])

  const fetchData = useCallback(
    async (questionId: number, callOnLoaded = false) => {
      const startDate = moment()
        .year(period.year)
        .month(period.month)
        .startOf('month')
        .format('YYYY-MM-DD')
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')
      try {
        const response = await statsCall({
          startDate,
          endDate,
          storeId,
          questionId,
        })
        if (response) {
          const grouped: { [level: number]: FoodPreferenceData[] } = {}
          const responseLevels = Array.from(new Set(response.map(item => item.level))).sort()
          responseLevels.forEach(level => {
            const levelData = response.filter((item: FoodPreferenceData) => item.level === level)
            const orderedData = [...levelData].sort((a, b) => b.value - a.value)

            const isEmptyData =
              orderedData.length === 1 &&
              orderedData[0].value === 0 &&
              orderedData[0].code === 'empty'
            const colorsToUse = isEmptyData ? emptyDataColor : foodColors

            grouped[level] = formatDonutChartData(
              orderedData.map((item: FoodPreferenceData, idx: number) => ({
                ...item,
                color: colorsToUse[idx % colorsToUse.length],
              })),
            )
          })
          setDataByLevel(grouped)
          setLevels(responseLevels)
        }
      } finally {
        if (callOnLoaded && onLoaded) {
          onLoaded()
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period, storeId, onLoaded],
  )

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await questionsCall({})
        setQuestions(response.userPolls)
        // Seleccionar la pregunta con defaultDashboard = true por defecto, o la primera activa si no hay ninguna marcada como default
        if (!selectedQuestionId) {
          const defaultQuestion = response.userPolls.find(q => q.active && q.defaultDashboard)
          if (defaultQuestion) {
            setSelectedQuestionId(defaultQuestion.id)
          } else {
            const firstActiveQuestion = response.userPolls.find(q => q.active)
            if (firstActiveQuestion) {
              setSelectedQuestionId(firstActiveQuestion.id)
            }
          }
        }
      } catch (error) {
        console.error('Error cargando preguntas:', error)
      }
    }
    fetchQuestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      if (selectedQuestionId) {
        fetchData(selectedQuestionId, true)
      } else {
        pendingInitialLoadRef.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    if (skipQuestionFetchRef.current) {
      skipQuestionFetchRef.current = false
      return
    }
    if (!canFetchRef.current || !selectedQuestionId) return
    const callOnLoaded = pendingInitialLoadRef.current
    pendingInitialLoadRef.current = false
    fetchData(selectedQuestionId, callOnLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionId])

  const downloadFoodPreferenceByLevelCSV = () => {
    const rows: string[][] = []
    Object.entries(dataByLevel).forEach(([level, data]) => {
      rows.push([`Nivel ${level}`])
      rows.push(['Categoría', 'Proporción (%)'])
      data.forEach(row => {
        rows.push([row.name, row.value !== undefined ? row.value.toFixed(2) : ''])
      })
      rows.push([''])
    })
    const csvContent = rows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'preferencia_comida_por_nivel.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
      <div className="flex flex-row justify-between items-center mb-6">
        <h3 className="text-base font-bold" style={montserratStyle}>
          Apertura por preferencias por nivel
        </h3>
        <IconButton
          disabled={statsLoading}
          className="ml-2"
          aria-label="download"
          onClick={downloadFoodPreferenceByLevelCSV}
        >
          <GetAppIcon />
        </IconButton>
      </div>

      {questions.length > 0 && (
        <div className="mb-6">
          <FormControl style={{ minWidth: 400 }}>
            <Select
              value={selectedQuestionId || ''}
              onChange={handleQuestionChange}
              style={{ ...montserratStyle }}
              IconComponent={KeyboardArrowDownRounded}
              disabled={questionsLoading}
            >
              {questionsLoading ? (
                <MenuItem value="">Cargando preguntas...</MenuItem>
              ) : (
                questions
                  .filter(q => q.active)
                  .map(question => (
                    <MenuItem key={question.id} value={question.id}>
                      {question.question}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
        </div>
      )}
      {statsLoading ? (
        <div className="flex flex-row gap-8">
          <div className="flex-1 flex items-center justify-center">
            <CircularProgress size={40} />
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-8">
          {levels.map(level => (
            <div key={`nivel-${level}`} className="flex-1">
              <DonutTagChart
                title=""
                chartTitle={`Nivel ${level}`}
                data={dataByLevel[level] || []}
                customTooltip={CustomTooltip}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

FoodPreferenceDonutChartsByLevel.defaultProps = {
  storeId: undefined,
}

export default FoodPreferenceDonutChartsByLevel
